"use client"

import { ButtonAddToCart } from "@/components/custom/Product/AddCart"
import { ProductVariants } from "@/components/molecules"
import { WishlistButton } from "@/components/cells/WishlistButton/WishlistButton"
import useGetAllSearchParams from "@/hooks/useGetAllSearchParams"
import { getProductPrice } from "@/lib/helpers/get-product-price"
import { buildSelectedVariantOptions, resolveVariantId } from "@/lib/utils"
import { SellerProps } from "@/types/seller"
import { Wishlist } from "@/types/wishlist"
import { HttpTypes } from "@medusajs/types"
import { useTranslate } from "@tolgee/react"
import { useMemo, useState } from "react"

export const BarebonesProductBuyBox = ({
  product,
  locale,
  user,
  wishlist,
}: {
  product: HttpTypes.StoreProduct & { seller?: SellerProps }
  locale: string
  user: HttpTypes.StoreCustomer | null
  wishlist?: Wishlist[]
}) => {
  const [isAdding, setIsAdding] = useState(false)
  const [descOpen, setDescOpen] = useState(true)
  const [shipOpen, setShipOpen] = useState(false)
  const { allSearchParams } = useGetAllSearchParams()
  const { t } = useTranslate()

  const selectedVariant = buildSelectedVariantOptions(product, allSearchParams)
  const variantId = resolveVariantId(product, selectedVariant)
  const { variant, variantPrice } = getProductPrice({ product, variantId })

  const variantStock =
    product.variants?.find(({ id }) => id === variantId)?.inventory_quantity ||
    0
  const variantHasPrice =
    typeof product.variants?.find(({ id }) => id === variantId)
      ?.calculated_price?.calculated_amount === "number"
  const isAvailable = !!variantStock && variantHasPrice && !isAdding

  const onSale =
    variantPrice?.calculated_price_number !==
    variantPrice?.original_price_number

  const descriptionPreview = useMemo(() => {
    const raw = (product.description || "").trim()
    if (!raw) return ""
    const first = raw.split(/\n\s*\n/)[0]?.replace(/\n/g, " ").trim() || raw
    return first.length > 280 ? `${first.slice(0, 277)}…` : first
  }, [product.description])

  return (
    <div className="bb-pdp-buybox flex flex-col text-[#2b2118]">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          {product.subtitle ? (
            <p className="mb-3 text-[11px] uppercase tracking-[0.18em] text-[#5c5148]">
              {product.subtitle}
            </p>
          ) : null}
          <h1 className="bb-serif text-3xl leading-tight md:text-[42px] md:leading-[1.12]">
            {product.title}
          </h1>
          <div className="mt-5 flex items-baseline gap-3">
            <span className="text-[22px] font-medium tracking-wide">
              {variantPrice?.calculated_price}
            </span>
            {onSale && (
              <span className="text-[15px] text-[#8a7f74] line-through">
                {variantPrice?.original_price}
              </span>
            )}
          </div>
        </div>
        <WishlistButton
          product={product}
          productId={product.id}
          wishlist={wishlist}
          user={user}
          skin="barebones"
        />
      </div>

      <div className="mt-8">
        {product.options &&
        product.options.length === 1 &&
        (product.options[0].values?.length || 0) <= 1 ? null : (
          <ProductVariants product={product} selectedVariant={selectedVariant} />
        )}
      </div>

      {!variantHasPrice ? (
        <p className="mt-4 text-sm text-[#8a3a2a]">
          {t("product_details.no_region_price")}
        </p>
      ) : !variantStock ? (
        <p className="mt-4 text-sm text-[#8a3a2a]">
          {t("product_details.out_of_stock")}
        </p>
      ) : null}

      <div className="mt-8">
        <ButtonAddToCart
          size="large"
          variant={variant}
          title={product.title}
          locale={locale}
          loading={isAdding}
          setLoading={setIsAdding}
          disabled={!isAvailable}
          thumbnail={product.thumbnail}
          className={
            "bb-atc flex h-14 w-full items-center justify-center rounded-full border border-[#2b2118] bg-[#f3eadb] text-[12px] font-medium uppercase tracking-[0.14em] text-[#2b2118] transition-colors hover:bg-[#2b2118] hover:text-[#f7f4ef] " +
            (isAvailable ? "" : "opacity-40")
          }
        >
          {variantStock && variantHasPrice
            ? `${t("product_details_header.add_to_cart")} — ${variantPrice?.calculated_price || ""}`
            : !variantHasPrice
              ? t("product_details.no_region_price")
              : t("product_details_header.out_of_stock")}
        </ButtonAddToCart>
        <p className="mt-4 text-center text-[11px] uppercase tracking-[0.14em] text-[#5c5148]">
          {t("barebones_pdp.free_shipping", "Free shipping on qualifying orders")}
        </p>
      </div>

      <div className="mt-10 border-t border-[#e4ddd2]">
        {descriptionPreview ? (
          <div className="border-b border-[#e4ddd2]">
            <button
              type="button"
              className="flex w-full items-center justify-between py-4 text-left text-[12px] font-medium uppercase tracking-[0.16em]"
              onClick={() => setDescOpen((v) => !v)}
              aria-expanded={descOpen}
            >
              {t("product_details.details_heading")}
              <span aria-hidden className="text-lg leading-none">
                {descOpen ? "−" : "+"}
              </span>
            </button>
            {descOpen && (
              <p className="pb-5 text-[14px] leading-relaxed text-[#5c5148]">
                {descriptionPreview}
              </p>
            )}
          </div>
        ) : null}

        <div className="border-b border-[#e4ddd2]">
          <button
            type="button"
            className="flex w-full items-center justify-between py-4 text-left text-[12px] font-medium uppercase tracking-[0.16em]"
            onClick={() => setShipOpen((v) => !v)}
            aria-expanded={shipOpen}
          >
            {t("product_details.shipping_returns_heading")}
            <span aria-hidden className="text-lg leading-none">
              {shipOpen ? "−" : "+"}
            </span>
          </button>
          {shipOpen && (
            <ul className="space-y-2 pb-5 text-[14px] leading-relaxed text-[#5c5148]">
              <li>{t("product_details.shipping_returns_description_1")}</li>
              <li>{t("product_details.shipping_returns_description_2")}</li>
            </ul>
          )}
        </div>
      </div>
    </div>
  )
}
